import React, { ReactElement, useEffect, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Badge,
} from "@chakra-ui/react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { CompanyType, getCompanySuffix } from "@/features/types";
import useListAllCompanies, {
  ListAllCompaniesPathParameter,
  ListAllCompaniesRequest,
} from "@/features/line-sheet-sets/hooks/use-list-all-companies";
import useIdentity from "@/features/ui/hooks/use-identity";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PersistedLineSheetSetListQuery } from "@/features/order-sheet-sets/agency-line-sheet-set-list-page";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { log } from "node:util";
import SVG from "react-inlinesvg";
import { useChipList } from "@/features/order-sheet-sets/hooks/use-chip-list";
import { SheetTabFilterQuery } from "@/features/order-sheet-sets/sheet-tab-filter";
import { PersistedLineSheetSetDetailPageQuery } from "@/features/line-sheet-sets/detail/use-persisted-line-sheet-sheet-set-detail-page-query";
import { OrderSheetSetPagePersistedQuery } from "@/features/order-sheet-sets/hooks/use-order-sheet-set-page-persisted-query";
import { NavigateOptions } from "react-router";
import { BestSellingSectionQuery } from "@/features/dashboard/features/best-selling-section/hooks/useBsQuery";

interface Props<T> {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  resetBtn?: ReactElement;
  queryCount?: number;
  setQueryCount: (count: number) => void;
  query: T;
  setQuery: any;
  useCustomChipList?: (query: any) => any;
}

const FilterModal = <T,>({
  isOpen,
  onClose,
  children,
  resetBtn,
  queryCount,
  query,
  setQuery,
  setQueryCount,
  useCustomChipList,
}: Props<T>) => {
  const { tTitle } = useI18nHelper();
  const useNewChipList = useCustomChipList ?? useChipList;
  const { chipList, refineQuery } = useNewChipList(
    query as PersistedLineSheetSetListQuery &
      SheetTabFilterQuery &
      PersistedLineSheetSetDetailPageQuery &
      BestSellingSectionQuery &
      any
  );

  useEffect(() => {
    setQueryCount(chipList?.length);
  }, [chipList, setQueryCount]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <Box background={"#F7F9FA"}>
          <ModalHeader>{tTitle("filter")}</ModalHeader>
        </Box>
        <ModalBody display="flex" flexDirection={"column"} gap={"24px"}>
          {children}
          <Flex gap={"12px"} flexWrap={"wrap"}>
            {chipList.map((item: any, index: number) => (
              <Badge
                key={`${item}-${index}`}
                borderRadius="4px"
                background={"#F7F9FA"}
                color={"#1272EF"}
                textStyle={"body-sm-medium"}
                display="flex"
                gap={"2px"}
              >
                {item.name}
                <SVG
                  src={"/icons-v1_1/x.svg"}
                  width={"20px"}
                  height={"20px"}
                  cursor={"pointer"}
                  onClick={() =>
                    setQuery((prev: any) => {
                      return {
                        ...prev,
                        ...refineQuery(item.type, item.id),
                        pageNumber: 1,
                      };
                    })
                  }
                />
              </Badge>
            ))}
          </Flex>
        </ModalBody>
        <ModalFooter display={"flex"} justifyContent={"space-between"}>
          {resetBtn}
          <Flex gap={"12px"}>
            <Button onClick={onClose}>{tTitle("close")}</Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FilterModal;
